import { Input, Directive, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';

import * as _ from 'lodash';
import { Subscription } from 'rxjs';
import { SyncService } from 'src/app/core/services';
import { IBaseFieldComponent } from 'src/app/interfaces/base-field-component.interface';
import { IBaseMutableModel } from 'src/app/interfaces/base-mutable-model.interface';
import { IFieldSettings } from 'src/app/interfaces/field-settings.interface';
import { IBaseTableCell } from 'src/app/interfaces/tables/cells/base-table-cell-interface';
import {
    TableFieldOption,
    CalibrationSetCollectionName,
    getCalibrationSetTypeId,
    TableType,
    CalibrationSetType
} from 'src/shared/enums';

@Directive()
export class BaseFieldComponent implements OnInit, OnDestroy, IBaseFieldComponent {
    subscription = new Subscription();

    @Input()
    model?: IBaseTableCell<any>;

    @Input()
    fieldSettings?: IFieldSettings;

    @Input()
    propertyName?: string;

    @Input()
    tableFieldOption?: TableFieldOption;

    @Input()
    tableFieldId?: number;

    @Input()
    currentTab?: string;

    @Input()
    attributePopupTitleDetails?: string;

    @Input()
    readOnly: boolean = false;

    @Input()
    hideAttributeButtons: boolean = false;

    private _calibrationSetTableFieldIdentifier: any;
    private _calibrationSetType: CalibrationSetType;

    constructor(public readonly syncService: SyncService, private readonly cd: ChangeDetectorRef) {}

    get modelValue(): IBaseMutableModel<any> {
        return _.get(this.model, 'value');
    }

    get calibrationSet() {
        return _.get(this.model, 'calibrationSet');
    }

    get errorMessages() {
        let requiredIgnore = false;
        if (this.currentTab) {
            requiredIgnore = this.currentTab !== CalibrationSetCollectionName.CALIBRATION_ZONE_COMBINATION;
        }
        return this.modelValue
            ? this.modelValue.getErrorMessagesForField(
                  this.property,
                  this.calibrationSet,
                  requiredIgnore || !(this.calibrationSet && this.calibrationSet.validateRequiredFields)
              )
            : [];
    }

    get isDisabled() {
        return (
            (this.fieldSettings ? this.fieldSettings.isDisabled(this.modelValue.currentState) : false) || this.readOnly
        );
    }

    get property() {
        return _.get(this, 'fieldSettings.name', this.propertyName);
    }

    get calibrationSetType() {
        if (!_.isNumber(this._calibrationSetType)) {
            this._calibrationSetType = this.calibrationSet
                ? this.calibrationSet.calibrationSetType
                : getCalibrationSetTypeId(this.currentTab);
        }
        return this._calibrationSetType;
    }

    get currentDisplayedValue(): any {
        return _.get(this, `modelValue.currentState[${this.property}]`);
    }

    hasErrors(): boolean {
        const modelValue = this.modelValue as any;
        return (
            !!this.errorMessages.length || modelValue?.importValidationResult?.fieldsWithIssues?.includes(this.property)
        );
    }

    getCalibrationSetTableFieldIdentifier() {
        if (!this._calibrationSetTableFieldIdentifier && this.modelValue) {
            const result: any = {
                calibrationSetId:
                    this.modelValue.currentState.ZoneCombinationId ||
                    this.modelValue.currentState.OverlayTypologyId ||
                    this.modelValue.currentState.OverlayId,
                calibrationSetType: this.calibrationSetType,
                tableField: { name: this.property },
                tableFieldOption: this.tableFieldOption || TableFieldOption.NONE
            };

            if (this.tableFieldId) {
                result.tableField = { Id: this.tableFieldId };
                result.tableType = TableType.CALIBRATION_USES;
            }
            this._calibrationSetTableFieldIdentifier = result;
        }
        return this._calibrationSetTableFieldIdentifier;
    }

    ngOnInit() {
        this.subscription.add(
            this.syncService.changeDetection.subscribe(() => {
                this.triggerChangeDetection();
            })
        );
        if (this.modelValue) {
            this.modelValue.registerPropertyEditorComponent(this.property, this);
        }
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    handleManualChange() {
        this.model.value.markManuallyChangedProperty(this.property);
    }

    handleValueChange() {
        if (this.model.value.getIsOriginalState(this.property)) {
            return;
        }
        this.syncService.emitValueChanged(this);
        const fieldSettings = this.fieldSettings;
        if (_.get(fieldSettings, 'jointValidationFieldsList')) {
            fieldSettings.jointValidationFieldsList.forEach(fieldName => {
                if (this.modelValue) {
                    this.modelValue.getErrorMessagesForField(fieldName, this.calibrationSet, false);
                    this.modelValue.triggerPropertyEditorChangeDetection(fieldName);
                }
            });
        }
    }

    triggerChangeDetection() {
        if (!this.cd) {
            return;
        }
        this.cd.detectChanges();
    }
}
